import React, { FC, useCallback, useMemo, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import {
	FileVariantsEnum,
	getEventPriceRangeString,
	formatEventTimeRange
} from '@sweatpals/shared';
import { useStyles, Showable, Colors, clsx } from '@sweatpals/react-ui-kit';
import { useRouter } from 'next/router';
import { communityEventStyles } from './community-event.styles';
import { getPublicFileUrl } from '../../../utils/file.utils';
import { CalendarIcon, LocationIcon } from '../../icons';
import LocationAddressLink from '../../links/LocationAddressLink';
import { EventType } from '../../../services/types/event-types';
import useLink from '../../../utils/hooks/useLink';
import { eventsService } from '../../../services';
import { FadeSpinner } from '../fade-spinner';

interface CommunityEventProps {
	event?: EventType;
	showSkeleton?: boolean;
}

const CommunityEvent: FC<CommunityEventProps> = ({ event, showSkeleton }) => {
	const [isOpeningEventPage, setIsOpeningEventPage] = useState(false);
	const disabled = useMemo(() => (showSkeleton || isOpeningEventPage), [showSkeleton, isOpeningEventPage]);
	const styles = useStyles(communityEventStyles, disabled);
	const { makeEventLink } = useLink();
	const router = useRouter();

	const openEventPage = useCallback(async (): Promise<void> => {
		if (!event) {
			return;
		}

		setIsOpeningEventPage(true);

		try {
			const eventId = event.baseId ?? event.id;
			const eventsCount = await eventsService.getEventsCountPublic({ eventIds: [eventId] });

			const eventLink = makeEventLink({
				eventAlias: event.alias,
				shortLocalInstance: eventsCount > 1 ? event.shortLocalInstance : undefined,
			});

			await router.push(eventLink);
		} finally {
			setIsOpeningEventPage(false);
		}
	}, [event, makeEventLink, router]);

	if (!showSkeleton && !event) {
		return null;
	}

	return (
		<Box
			sx={styles.root}
			onClick={!disabled ? openEventPage : undefined}
		>
			<Showable show={showSkeleton}>
				<Skeleton sx={clsx(styles.avatar, { backgroundColor: Colors.white_a30 })} variant={'rectangular'} />
				<Box sx={styles.data}>
					<Skeleton sx={clsx(styles.name, { backgroundColor: Colors.white_a30 })} variant={'text'} />
					<Skeleton sx={{ height: 24, backgroundColor: Colors.white_a30 }} variant={'rounded'} />
					<Skeleton sx={{ height: 24, backgroundColor: Colors.white_a30 }} variant={'rounded'} />
				</Box>
				<Skeleton sx={clsx(styles.price, { backgroundColor: Colors.white_a30 })} variant={'rounded'} />
			</Showable>
			<Showable show={event && !showSkeleton}>
				{() => (
					<>
						<Showable show={isOpeningEventPage}>
							<Box sx={styles.loader}>
								<FadeSpinner size={60} color="white" />
							</Box>
						</Showable>
						<Box
							sx={{
								...styles.avatar,
								backgroundImage: event.background ? `url(${getPublicFileUrl(event.background, { variant: FileVariantsEnum.S })})` : undefined,
							}}
						/>
						<Box sx={styles.data}>
							<Box sx={styles.name}>{event.name}</Box>
							<Showable show={!event.additionalData?.hideEventDate}>
								<Box sx={styles.item} mb="10px">
									<CalendarIcon />
									<Typography sx={styles.itemText} suppressHydrationWarning>
										{formatEventTimeRange(event.startDate, event.endDate, {
											timezone: event.tzid,
											startDateFormat: 'eee MMM do, h:mm aa',
											endDateFormat: 'eee MMM do, h:mm aa'
										})}
									</Typography>
								</Box>
							</Showable>
							<Box sx={styles.item}>
								<LocationIcon />
								<Typography sx={styles.itemText}>
									{event.isOnlineEvent ? 'Online event' : <LocationAddressLink address={event.addressName} />}
								</Typography>
							</Box>
						</Box>
						<Box sx={styles.price}>
							{event.isPaid ? event.isDonationBased ? 'Donation-based' : getEventPriceRangeString(event.prices) : 'Free'}
						</Box>
					</>
				)}
			</Showable>
		</Box>
	);
};

export default CommunityEvent;
